import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Box, Typography } from "@mui/material"
import BaseImage from "../BaseImage"
import BaseLink from "../BaseLink"
import ReactDataTable from "../ReactDataTable"
import { capitalizeFirstLetter } from "../../hooks/use-capitalize-first-letter"
import { getStorage, setStorage } from "../../hooks/use-local-storage"
import { sortPatients } from "../../utils/patient-sort"

const statusStyles = {
  PENDING: { bg: '#FFF3CD', color: '#856404', label: 'Unpaid' },
  COMPLETED: { bg: '#D4EDDA', color: '#155724', label: 'Completed' },
  EXPIRED: { bg: '#F8D7DA', color: '#721C24', label: 'Expired' },
  CANCELLED: { bg: '#E2E3E5', color: '#383D41', label: 'Cancelled' },
  FAILED: { bg: '#F8D7DA', color: '#721C24', label: 'Failed' },
  PAID: { bg: '#D4EDDA', color: '#155724', label: 'Paid' },
}

/**
 * PatientTable - Combined table for active and pending patients (desktop view)
 * Uses patientType discriminator to conditionally render cells:
 *   - 'active': registered patients with reports
 *   - 'pending': invited patients awaiting registration
 */
const PatientTable = ({ rowData, onRowClicked }) => {
  const auth = useSelector(state => state.auth)
  const isStaffPhysician = useSelector(state => state.isStaffPhysician)
  const isAdminStaffPhysician = useSelector(state => state.isAdminStaffPhysician)
  const loadingAuth = useSelector(state => state.loadingAuth)
  const STAFF_VIEW_ROLES = ['MANAGER', 'SALES']
  const isStaff = isStaffPhysician || isAdminStaffPhysician ||
    auth?.roles?.some(r => STAFF_VIEW_ROLES.includes(r))

  const currentPatientTablePage = getStorage('currentPatientTablePage') || 1
  const currentRowsPerPage = getStorage('currentPatientTableRowsPerPage') || 10
  const [sortColumn, setSortColumn] = useState(() => getStorage('patientTableSortColumn') || null)
  const [sortDirection, setSortDirection] = useState(() => getStorage('patientTableSortDirection') || 'asc')

  const sortedRowData = useMemo(
    () => sortPatients(rowData, sortColumn, sortDirection),
    [rowData, sortColumn, sortDirection]
  )

  const columns = useMemo(() => {
    // Column widths as percentages so they scale proportionally.
    // Staff view (9 cols): USER 22%, FELLOW 12%, KIT 14%, DOB 12%, GENDER 9%, IN REVIEW 8%, TOTAL 8%, PAYMENT 8%, Reports 7%
    // Non-staff view (8 cols): USER 26%, KIT 15%, DOB 13%, GENDER 10%, IN REVIEW 9%, TOTAL 9%, PAYMENT 9%, Reports 9%
    const cols = [
      {
        name: 'USER',
        id: 'name',
        field: 'name',
        selector: row => row?.name,
        sortable: true,
        allowRowEvents: true,
        width: isStaff ? '22%' : '26%',
        cell: (row) => (
          <Box data-tag="allowRowEvents" sx={{ display: 'flex' }}>
            {row?.patientType === 'active' && (
              <BaseImage
                sx={{
                  minWidth: '40px',
                  width: '40px',
                  height: '40px',
                }}
                sximage={{
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
                src={row?.avatarUrl || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
                alt='user-avatar'
              />
            )}
            <Box data-tag="allowRowEvents" sx={{ ml: row?.patientType === 'active' ? '10px' : 0, overflow: 'hidden' }}>
              <Typography data-tag="allowRowEvents" variant="h3" noWrap>
                {row?.name}
              </Typography>
              <Typography data-tag="allowRowEvents" variant="body" color="primary.darkGray" fontSize="11px" noWrap>
                {row?.email}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        name: 'FELLOW',
        id: 'physicianName',
        field: 'physicianName',
        selector: row => row?.physicianName,
        sortable: true,
        allowRowEvents: true,
        width: '12%',
        omit: !isStaff,
        cell: (row) => (
          <Box data-tag="allowRowEvents">
            <Typography data-tag="allowRowEvents" variant="body" color="primary.darkGray" fontSize="11px">
              {row?.physicianName || '-'}
            </Typography>
          </Box>
        ),
      },
      {
        name: '#KIT CODE',
        id: 'kitCode',
        sortable: true,
        allowRowEvents: true,
        field: 'kitCode',
        selector: row => row?.kitCode,
        width: isStaff ? '14%' : '15%',
        cell: (row) => (
          <Box data-tag="allowRowEvents">
            <Typography data-tag="allowRowEvents" variant="body" color="primary.darkGray" fontFamily="monospace">
              {row?.kitCode}
            </Typography>
          </Box>
        ),
      },
      {
        name: 'DATE OF BIRTH',
        id: 'dateOfBirth',
        sortable: true,
        allowRowEvents: true,
        field: 'dateOfBirth',
        selector: row => row?.dateOfBirth,
        width: isStaff ? '12%' : '13%',
        cell: (row) => (
          <Box data-tag="allowRowEvents" sx={{ textAlign: 'center' }}>
            <Typography data-tag="allowRowEvents" variant="body" color="primary.darkGray" whiteSpace="nowrap" fontFamily="monospace">
              {row?.dateOfBirth}
            </Typography>
          </Box>
        ),
      },
      {
        name: 'GENDER',
        width: isStaff ? '9%' : '10%',
        allowRowEvents: true,
        selector: row => row?.sex,
        cell: (row) => (
          <Box
            data-tag="allowRowEvents"
            sx={{
              display: row?.sex ? 'block' : 'none',
              bgcolor: 'primary.lightGreen1',
              padding: '2px 8px',
              borderRadius: '4px',
              textAlign: 'center'
            }}
          >
            <Typography data-tag="allowRowEvents" variant="h4" color="primary.black" fontSize="13px">
              {capitalizeFirstLetter(row?.sex)}
            </Typography>
          </Box>
        ),
      },
      {
        name: <span style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>IN REVIEW</span>,
        id: 'reviewCount',
        width: isStaff ? '8%' : '9%',
        sortable: true,
        allowRowEvents: true,
        field: 'reviewCount',
        selector: row => row?.reviewCount,
        cell: (row) => (
          <Box data-tag="allowRowEvents" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Typography data-tag="allowRowEvents" variant="h4" color="primary.black">
              {row?.patientType === 'active' ? row?.reviewCount : '-'}
            </Typography>
          </Box>
        )
      },
      {
        name: <span style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>TOTAL REPORTS</span>,
        id: 'totalReportCounts',
        width: isStaff ? '8%' : '9%',
        sortable: true,
        allowRowEvents: true,
        field: 'totalReportCounts',
        selector: row => row?.totalReportCounts,
        cell: (row) => (
          <Box data-tag="allowRowEvents" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Typography data-tag="allowRowEvents" variant="h4" color="primary.black">
              {row?.patientType === 'active' ? row?.totalReportCounts : '-'}
            </Typography>
          </Box>
        )
      },
      {
        name: 'PAYMENT',
        id: 'status',
        sortable: true,
        allowRowEvents: true,
        field: 'status',
        selector: row => row?.status,
        width: isStaff ? '8%' : '9%',
        cell: (row) => {
          const statusKey = row?.patientType === 'active' ? 'PAID' : row?.status
          if (!statusKey) return null
          const style = statusStyles[statusKey] || statusStyles.PENDING
          return (
            <Box
              data-tag="allowRowEvents"
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                borderRadius: '3px',
                backgroundColor: style.bg,
                color: style.color,
                fontSize: '11px',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              {style.label}
            </Box>
          )
        },
      },
      {
        name: '',
        width: isStaff ? '7%' : '9%',
        cell: (row) => {
          if (row?.patientType !== 'active') return null
          return (
            <Box sx={{ width: '100%', textAlign: 'center', cursor: 'pointer' }}>
              <BaseLink to={`/patient/${row?.id}`}>
                <Typography variant="h4" color="primary.mainBlue" whiteSpace="nowrap">
                  Reports
                </Typography>
              </BaseLink>
            </Box>
          )
        },
      },
    ]

    return cols
  }, [isStaff])

  const conditionalRowStyles = useMemo(() => [
    {
      when: row => row?.patientType === 'pending',
      style: {
        backgroundColor: '#FFF3D3',
        cursor: 'pointer',
      },
    },
  ], [])

  const handleSort = (column, newDirection) => {
    setSortColumn(column.field)
    setSortDirection(newDirection)
    setStorage('patientTableSortColumn', column.field)
    setStorage('patientTableSortDirection', newDirection)
  }

  const handleChangePage = (page) => {
    setStorage('currentPatientTablePage', page)
  }

  const handleRowsPerPageChange = (rowsPerPage) => {
    setStorage('currentPatientTableRowsPerPage', rowsPerPage)
  }

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
      }}
    >
      <ReactDataTable
        columnDefs={columns}
        rowData={sortedRowData}
        sortServer
        defaultSortFieldId={sortColumn}
        defaultSortAsc={sortDirection === 'asc'}
        isPagination
        paginationPosition='top'
        loadingData={loadingAuth}
        paginationDefaultPage={parseInt(currentPatientTablePage)}
        defaultRowsPerPage={currentRowsPerPage}
        noText='No patient results found'
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleRowsPerPageChange}
        onSort={handleSort}
        onRowClicked={onRowClicked}
        conditionalRowStyles={conditionalRowStyles}
      />
    </Box>
  )
}

export default PatientTable
